import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, GuildChannelSummary } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface CreateChannelInput {
  name: string;
  description?: string;
  isPrivate?: boolean;
  isReadOnly?: boolean;
}

interface UpdateChannelInput {
  name?: string;
  description?: string | null;
  isPrivate?: boolean;
  isReadOnly?: boolean;
}

interface UseGuildChannelsReturn {
  channels: GuildChannelSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createChannel: (input: CreateChannelInput) => Promise<string>;
  updateChannel: (channelId: string, input: UpdateChannelInput) => Promise<void>;
  deleteChannel: (channelId: string) => Promise<void>;
}

interface UseGuildChannelsOptions {
  /**
   * Poll the list so unread badges stay live.
   *
   * Opt-in because only the rail renders badges. The guild page has no chat
   * WebSocket of its own — `useChat` connects one, but only while a channel is
   * actually open in the centre pane, and it refreshes the CHAT arche's caches,
   * not this one. So a user parked on the "#activity" view would otherwise see
   * no new-message signal until the window regained focus, which is precisely
   * the case the badges exist for. This mirrors the worktop/header badge, which
   * is kept fresh the same way. React Query pauses the interval while the tab
   * is hidden. Replacing the poll with a shared WS subscription is the real fix.
   */
  pollForUnread?: boolean;
}

/** Matches the query client's 30s staleTime — see `pollForUnread`. */
const UNREAD_POLL_INTERVAL_MS = 30_000;

/**
 * Loads and mutates the channels of a guild (Discord/Slack-style multi-channel).
 * The list already reflects per-user visibility (public channels + private ones
 * the caller belongs to) and carries the caller's unread state; mutations are
 * guild-admin-gated server-side.
 */
export function useGuildChannels(
  guildId: string | undefined,
  options: UseGuildChannelsOptions = {},
): UseGuildChannelsReturn {
  const queryClient = useQueryClient();
  const channelsQueryKey = queryKeys.guilds.channels(guildId ?? '');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: channelsQueryKey,
    queryFn: () => api.listGuildChannels(guildId!),
    enabled: !!guildId,
    refetchInterval: options.pollForUnread ? UNREAD_POLL_INTERVAL_MS : false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    // Guild channels also appear in the chat arche's sidebar (both surfaces
    // list the same `chat_channels` rows), so its cache is stale too.
    queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
  };

  const createMut = useMutation({
    mutationFn: (input: CreateChannelInput) => api.createGuildChannel(guildId!, input),
    onSuccess: invalidate,
  });

  const updateMut = useMutation({
    mutationFn: ({ channelId, input }: { channelId: string; input: UpdateChannelInput }) =>
      api.updateGuildChannel(guildId!, channelId, input),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (channelId: string) => api.deleteGuildChannel(guildId!, channelId),
    onSuccess: invalidate,
  });

  return {
    channels: data?.channels ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load channels' : null,
    refresh: () => {
      refetch();
    },
    createChannel: async (input: CreateChannelInput) => {
      const result = await createMut.mutateAsync(input);
      return result.channelId;
    },
    updateChannel: async (channelId: string, input: UpdateChannelInput) => {
      await updateMut.mutateAsync({ channelId, input });
    },
    deleteChannel: async (channelId: string) => {
      await deleteMut.mutateAsync(channelId);
    },
  };
}
